import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult } from '@/shared/analytics/AnalyticsTypes';
import { formatCount, formatPercent, primaryKeyLabel } from '@/shared/analytics/MetricFormatting';

export type TopicCalloutTone = 'headline' | 'opportunity' | 'watch';

/** A short, human-readable takeaway derived from an aggregate result. */
export interface TopicCallout {
    tone: TopicCalloutTone;
    text: string;
}

const LOW_SHARE_THRESHOLD = 0.04;

/**
 * Derives actionable takeaways from a single-field aggregate. Generic across all
 * insights — it reads concentration, the leader, the runner-up gap, and the
 * long tail rather than hand-writing prose per topic.
 */
export function deriveCallouts(definition: TopicDefinition, result: AdHocAggregateResult): TopicCallout[] {
    const metric = definition.headlineMetric;
    const total = result.totals[metric];
    const rows = result.rows;
    if (rows.length === 0 || total === 0) return [];

    const callouts: TopicCallout[] = [];
    const first = rows[0];
    if (first !== undefined) {
        const label = primaryKeyLabel(definition.primaryField, first.keys[definition.primaryField] ?? '—');
        const share = first.metrics[metric] / total;
        callouts.push({
            tone: 'headline',
            text: `${label} leads with ${formatCount(first.metrics[metric])} ${definition.unitLabel} — ${formatPercent(share)} of the total.`,
        });
    }

    const second = rows[1];
    if (first !== undefined && second !== undefined) {
        const ratio = second.metrics[metric] === 0 ? 0 : first.metrics[metric] / second.metrics[metric];
        if (ratio >= 1.5) {
            callouts.push({
                tone: 'opportunity',
                text: `The top ${definition.lensLabel} sees ${ratio.toFixed(1)}× the activity of the next — engagement is concentrated, not spread.`,
            });
        }
    }

    const topThree = rows.slice(0, 3).reduce((sum, row) => sum + row.metrics[metric], 0);
    if (rows.length >= 4) {
        callouts.push({
            tone: 'opportunity',
            text: `The top 3 ${definition.lensPlural} account for ${formatPercent(topThree / total)} of all ${definition.unitLabel}.`,
        });
    }

    const longTail = rows.filter((row) => row.metrics[metric] / total < LOW_SHARE_THRESHOLD).length;
    if (longTail >= 3) {
        callouts.push({
            tone: 'watch',
            text: `${longTail} ${definition.lensPlural} barely register (<${formatPercent(LOW_SHARE_THRESHOLD)} each) — a long tail worth a closer look.`,
        });
    }

    return callouts;
}
